import { OnrampApiError } from '../client.js';
export function createCryptoDepositController(client, surface, locale, createId = () => crypto.randomUUID(), initialOperationId = null) {
    let snapshot = {
        operation: null,
        selectedRoute: null,
        busy: false,
        checking: false,
        checkedWithoutTransfer: false,
        error: null,
    };
    let idempotencyKey = null;
    let requestedOperationId = initialOperationId;
    let disposed = false;
    let inFlight = false;
    let retryNotBefore = 0;
    let refreshBackoffMs = 7_000;
    const listeners = new Set();
    const completionListeners = new Set();
    let completedOperationId = null;
    const update = (patch) => {
        if (disposed)
            return;
        snapshot = { ...snapshot, ...patch };
        for (const listener of listeners)
            listener();
        if (snapshot.operation?.state === 'complete' &&
            snapshot.operation.operationId !== completedOperationId) {
            completedOperationId = snapshot.operation.operationId;
            for (const listener of completionListeners)
                listener(completedOperationId);
        }
    };
    const provider = () => {
        if (!client.cryptoDeposit)
            throw new Error('crypto_deposit_unavailable');
        return client.cryptoDeposit;
    };
    const run = async (action, polling = false) => {
        if (inFlight || disposed || (polling && Date.now() < retryNotBefore))
            return;
        inFlight = true;
        update({ busy: true, error: null });
        try {
            await action();
            if (polling) {
                retryNotBefore = 0;
                refreshBackoffMs = 7_000;
            }
        }
        catch (error) {
            if (polling) {
                const retryAfter = error instanceof OnrampApiError ? error.retryAfterMs : 0;
                retryNotBefore = Date.now() + Math.max(refreshBackoffMs, retryAfter);
                refreshBackoffMs = Math.min(refreshBackoffMs * 2, 60_000);
            }
            update({ error: 'unavailable' });
        }
        finally {
            inFlight = false;
            update({ busy: false });
        }
    };
    const start = () => {
        requestedOperationId = null;
        return run(async () => {
            idempotencyKey ??= createId();
            const operation = await provider().create({ idempotencyKey, surface, locale });
            update({
                operation,
                selectedRoute: operation.routes[0] ?? null,
                checkedWithoutTransfer: false,
            });
        });
    };
    const resume = (id) => {
        requestedOperationId = id;
        return run(async () => {
            const operation = await provider().get(id);
            update({
                operation,
                selectedRoute: operation.instructions?.route ?? operation.routes[0] ?? null,
                checkedWithoutTransfer: false,
            });
        });
    };
    return {
        getSnapshot: () => snapshot,
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        start,
        resume,
        retry: () => (requestedOperationId ? resume(requestedOperationId) : start()),
        select(routeId) {
            if (snapshot.operation?.instructions)
                return;
            const selectedRoute = snapshot.operation?.routes.find((route) => route.id === routeId);
            if (selectedRoute)
                update({ selectedRoute, error: null, checkedWithoutTransfer: false });
        },
        continue: () => run(async () => {
            const { operation, selectedRoute } = snapshot;
            if (!operation || !selectedRoute) {
                update({ error: 'route_unavailable' });
                return;
            }
            update({
                operation: await provider().instructions(operation.operationId, selectedRoute.id),
                checkedWithoutTransfer: false,
            });
        }),
        refresh: () => run(async () => {
            if (snapshot.operation) {
                const operation = await provider().get(snapshot.operation.operationId);
                update({
                    operation,
                    checkedWithoutTransfer: snapshot.checkedWithoutTransfer &&
                        operation.state === 'deposit_address' &&
                        operation.transfers.length === 0,
                });
            }
        }, true),
        reconcile: () => {
            update({ checking: true });
            return run(async () => {
                if (snapshot.operation) {
                    const operation = await provider().reconcile(snapshot.operation.operationId);
                    update({
                        operation,
                        checkedWithoutTransfer: operation.state === 'deposit_address' && operation.transfers.length === 0,
                    });
                }
            }).finally(() => update({ checking: false }));
        },
        onComplete(listener) {
            completionListeners.add(listener);
            return () => {
                completionListeners.delete(listener);
            };
        },
        dispose() {
            disposed = true;
            listeners.clear();
            completionListeners.clear();
        },
    };
}
